import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { translate } from '../translations/translations';
import querystring from 'querystring';

import config from 'src/config';

import { SSORoutes } from 'src/enums/routes';
import UserModel from 'src/pods/user/models/UserModel';
import SessionModel from 'src/pods/sessions/models/SessionModel';
import { PractitionerSessionStatus } from 'src/enums/session.status';
import JournalModel from 'src/pods/journals/models/JournalModel';
import { toggleErrorModal } from 'src/pods/system/state/actions';
import ApiCalls from 'src/pods/user/api/ApiCalls';
import { getPractitioner, getUser } from 'src/pods/user/state/actions';
import {
    CustomFiltering,
    CustomFilteringInactive,
    Disease,
    Genes,
    Intermediate,
    LevelOfEvidence,
    Metabolizer,
    Normal,
    PhenotypeCategory,
    PillIcon,
    Poor,
    QuickView,
    QuickViewInactive,
    Rapid,
    SouthAfrica,
    SpecialtyPopulation,
    TableUserAvatar,
    TherapeuticClass,
    TherapyCategory,
    USflag,
} from 'src/assets/images/custom';
import OnboardingModel from 'src/pods/onboarding/models/OnboardingModel';
import WarehouseModel from 'src/pods/warehousing/models/WarehouseModel';
import PaymentModel from 'src/pods/settings/models/PaymentModel';
import { IPaymentType, PaymentTypes } from 'src/enums/payment.types';
import WorkspaceModel from 'src/pods/warehousing/models/WarehouseModel';
import AddressModel from 'src/pods/settings/models/AdressModel';
import { ICurrency } from 'src/enums/currencies';
import { IOptionType } from 'src/pods/system/models/OptionModel';
import PractitionerCategoryModel from 'src/pods/user/models/PractitionerCategoryModel';
import KitModel from 'src/pods/warehousing/models/KitModel';
import CollectionModel from 'src/pods/sessions/models/CollectionModel';
import PriceRuleModel from 'src/pods/discount/models/PriceRuleModel';
import { ScoreCategory } from 'src/enums/score.categories';
import { ScoreNames } from 'src/enums/score.names';
import { SessionStatusCategories } from 'src/enums/session.status.categories';
import ResultModel from 'src/pods/sessions/models/ResultModel';
import BioCerticaCollectionModel from 'src/pods/sessions/models/BioCerticaCollectionModel';
import { BadgeVariantsType } from 'src/components/v2/Badge';
import QuestionnarieModel from 'src/pods/sessions/models/QuestionarieModel';
import { MetabolizerStatuses } from 'src/enums/metabolizer.statuses';
import theme from 'src/styles/theme';
import { ReportsTypes } from 'src/enums/reports.types';
import { IFilteringOption } from 'src/pods/sessions/interfaces';
import { IPaginator } from 'src/pods/system/interfaces';
import LanguageModel from '../pods/user/models/LanguageModel';
import PractitionerSpecializationModel from '../pods/user/models/PractitionerSpecializationModel';

const emailRegex = /^.+@.+$/;
const numberRegex = /^[0-9]+$/;

export function createQueryString(paginator: any) {
    return querystring.stringify(paginator);
}

export const isNullOrUndefined = (value?: unknown): boolean => {
    return value === undefined || value === null;
};

export const isArrayEmpty = (value?: any): boolean => {
    return value === undefined || value.length === 0;
};

export const isNotNullOrUndefined = (value?: unknown): boolean => {
    return !isNullOrUndefined(value);
};

export const isBoolean = (value: unknown): boolean => {
    return typeof value === 'boolean';
};

export const isNotBlank = (value?: string): boolean => {
    if (value) {
        return !isNullOrUndefined(value) && value.length > 0;
    }
    return false;
};

export const isEmail = (value?: string): boolean => {
    if (value) {
        return isNotNullOrUndefined(value) && emailRegex.test(value);
    }
    return false;
};

export const isNumber = (value?: string): boolean => {
    if (value) {
        return isNotNullOrUndefined(value) && numberRegex.test(value);
    }
    return false;
};

export function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export const truncate = (str: string | undefined, n: number, ellipsis: string) => {
    if (!str) {
        return '';
    }

    const tooLong = str.length > n;
    const singular = str.search(/\s/) === -1;

    // Edge case where someone enters a ridiculously long string.
    str = tooLong ? str.substr(0, n - 1) : str;

    if (!singular) {
        str = ellipsis && tooLong ? str.substr(0, str.lastIndexOf(' ')) : str;
    }

    return tooLong ? str + ellipsis : str;
};

export const range = (min: number, max: number): Array<number> => {
    // eslint-disable-next-line prefer-spread
    return Array.apply(null, Array(max - min + 1)).map(function (_, n) {
        return n + min;
    });
};

export const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export const parseError = (error: any, fallbackErrorMessage = '') => {
    const errorData = error?.response?.data?.error;

    if (errorData) {
        if (errorData.code) {
            return translate(`errors.${errorData.code}`);
        }

        if (errorData.message) {
            return errorData.message;
        }
    }

    if (error.message) {
        return error.message;
    }

    return fallbackErrorMessage;
};

export const mapKitsToOptions = (kits: KitModel[]): IOptionType[] => {
    return kits.map((kit, index) => ({ key: index, value: index, label: kit.id.toString() }));
};

export const mapCollectionsToOptions = (collections: CollectionModel[]): IOptionType[] => {
    return collections.map((c) => ({ value: c.id, label: c.title }));
};

export const mapPriceRulesToOptions = (priceRules: PriceRuleModel[]): IOptionType[] => {
    return priceRules.map((rule, index) => ({ value: index, label: rule.title }));
};

export const mapUsersToOptions = (users: UserModel[]): IOptionType[] => {
    return users.map((user) => ({ value: user.id, label: getUserFullName(user) }));
};

export const createSSOAuthRoute = (routeName: SSORoutes) => {
    return `${config.SSO_APP_URL}/${routeName}?redirectURI=${config.PRACTITIONERS_APP_URL}`;
};

export const checkIfJournalIsSubmitted = (journalSubmittedAt: string) => {
    return journalSubmittedAt === null ? translate('journal.status.saved') : translate('journal.status.submitted');
};

export const disableEditJournalDataIfAlreadySubmitted = (journalSubmittedAt: string) => {
    return journalSubmittedAt === null ? false : true;
};

export const getUserFullName = (user: UserModel) => {
    return `${user.firstName} ${user.lastName}`;
};

export const checkIfUserCanOpenSessionDetails = (session: SessionModel) => {
    return (
        session.status === PractitionerSessionStatus.SESSION_STATUS_COMPLETED ||
        session.status === PractitionerSessionStatus.SESSION_STATUS_DECLINED
    );
};

export const journalExistsAlready = (journals: JournalModel[], session: SessionModel) => {
    return journals.filter((x) => x.sessionID === session.id).length > 0;
};

export const getJournalID = (journals: JournalModel[], session: SessionModel) => {
    return journals.filter((x) => x.sessionID === session.id)[0].id;
};

export const acceptTos = async (dispatch: any) => {
    try {
        await ApiCalls.acceptLastCreatedToS();
        dispatch(getUser());
    } catch (err) {
        const errorMessage = parseError(err, 'Error occured while accepting ToS.');
        dispatch(toggleErrorModal(translate('errors.GENERAL_ERROR_TITLE'), errorMessage));
    }
};

export const acceptPortalTos = async (dispatch: any) => {
    try {
        await ApiCalls.acceptLastCreatedPractitionerToS();
        dispatch(getPractitioner());
    } catch (err) {
        const errorMessage = parseError(err, 'Error occured while accepting ToS.');
        dispatch(toggleErrorModal(translate('errors.GENERAL_ERROR_TITLE'), errorMessage));
    }
};

export const getPractitionerToS = async (setTosData: any, dispatch: any) => {
    try {
        const response = await ApiCalls.getLastPractitionerToS();
        const tosURL = response?.data?.tosURI;

        fetch(tosURL, {
            method: 'GET',
        })
            .then((response: any) => response.arrayBuffer())
            .then((arrayBuffer: any) => {
                const buffer = new Uint8Array(arrayBuffer);
                setTosData(buffer);
            });
    } catch (err) {
        const errorMessage = parseError(err, 'Error occured while getting ToS file.');
        dispatch(toggleErrorModal(translate('errors.GENERAL_ERROR_TITLE'), errorMessage));
    }
};

export const getToS = async (setTosData: any, dispatch: any) => {
    try {
        const response = await ApiCalls.getLastToS();
        const tosURL = response?.data?.tosURI;

        fetch(tosURL, {
            method: 'GET',
        })
            .then((response: any) => response.arrayBuffer())
            .then((arrayBuffer: any) => {
                const buffer = new Uint8Array(arrayBuffer);
                setTosData(buffer);
            });
    } catch (err) {
        const errorMessage = parseError(err, 'Error occured while getting ToS file.');
        dispatch(toggleErrorModal(translate('errors.GENERAL_ERROR_TITLE'), errorMessage));
    }
};

export function setUserProfileImageIFEmpty(profileImg: any) {
    return profileImg ? (profileImg === '' ? TableUserAvatar : profileImg) : TableUserAvatar;
}

export const canProceedToNextStep = (firstValue: string | undefined, secondValue: string | undefined) => {
    return isNotBlank(firstValue) && isNotBlank(secondValue);
};

export const canCompleteOnboarding = (
    hpcsa: string,
    practiceNumber: string,
    category: number | undefined,
    description: string,
) => {
    return (hpcsa === '' || practiceNumber === '') && (category === undefined || description === '') ? true : false;
};

export const canUserCompleteOnboarding = (
    onboardingModel: OnboardingModel,
    description: string | undefined,
    categoryID: number | undefined,
    profileImg: string | undefined,
) => {
    return (
        canProceedToNextStep(onboardingModel.bkNumber, onboardingModel.practiceNumber) &&
        isNotBlank(description) &&
        isNotBlank(profileImg) &&
        isNotNullOrUndefined(categoryID)
    );
};

export const createWarehouseModel = (warehouse: Partial<WarehouseModel>) => {
    return {
        contactNumber: warehouse.contactNumber,
        contactPerson: warehouse.contactPerson,
        countryCode: warehouse.countryCode,
        name: warehouse.name,
        postalCode: warehouse.postalCode,
        state: warehouse.state,
        streetAddress: warehouse.streetAddress,
        suburb: warehouse.suburb,
    };
};

export const isAddressUndefined = (address: AddressModel) => {
    return (
        isNullOrUndefined(address.address) &&
        isNullOrUndefined(address.createdAt) &&
        isNullOrUndefined(address.id) &&
        isNullOrUndefined(address.latitude) &&
        isNullOrUndefined(address.longitude) &&
        isNullOrUndefined(address.practitionerID) &&
        isNullOrUndefined(address.primary) &&
        isNullOrUndefined(address.updatedAt)
    );
};

export const getIdentifierPlaceholder = (paymentDetails: Partial<PaymentModel>) => {
    return paymentDetails.type === PaymentTypes[0].id
        ? 'Bank account number'
        : paymentDetails.type === PaymentTypes[1].id
        ? 'PayPal account mail or phone number'
        : 'Please select type of payment first.';
};

export const formatDateAndTime = (dateTime: string) => {
    const m = moment(dateTime, 'YYYY-MM-DD HH:mm:ss');
    return `${m.format('DD MMM')}`;
};

export const getMonthLabel = (index: number) => {
    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    return monthNames[index].slice(0, 3);
};

export const isWarehouseUndefined = (obj: Partial<WorkspaceModel>) => {
    return (
        isNullOrUndefined(obj.contactNumber) ||
        isNullOrUndefined(obj.contactPerson) ||
        isNullOrUndefined(obj.countryCode) ||
        isNullOrUndefined(obj.name) ||
        isNullOrUndefined(obj.postalCode) ||
        isNullOrUndefined(obj.state) ||
        isNullOrUndefined(obj.streetAddress) ||
        isNullOrUndefined(obj.suburb)
    );
};

export const isWarehouseEmpty = (obj: Partial<WorkspaceModel>) => {
    return (
        obj.contactNumber === '' ||
        obj.contactPerson === '' ||
        obj.countryCode === '' ||
        obj.name === '' ||
        obj.postalCode === '' ||
        obj.state === '' ||
        obj.streetAddress === '' ||
        obj.suburb === ''
    );
};

export const mapPractitionerCategoriesToOptions = (categories: PractitionerCategoryModel[]): IOptionType[] => {
    return categories.map((c) => ({ value: c.id, label: c.name }));
};

export const mapCurrenciesToOptions = (categories: ICurrency[]): IOptionType[] => {
    return categories.map((c) => ({ value: c.code, label: c.code + ' - ' + c.name }));
};

export const mapPaymentTypesToOptions = (categories: IPaymentType[]): IOptionType[] => {
    return categories.map((c) => ({ value: c.id, label: c.name }));
};

export const mapLanguagesToOptions = (languages: LanguageModel[]): IOptionType[] => {
    return languages.map((lang) => ({ value: lang.id, label: lang.name }));
};

export const mapSpecializationsToOptions = (specializations: PractitionerSpecializationModel[]): IOptionType[] => {
    return specializations.map((s) => ({ value: s.id, label: s.specialization }));
};

export const getPractitionerTypeNameByID = (categories: PractitionerCategoryModel[], id: number) => {
    const match = categories.find((el) => el.id === id);
    return match?.name ? match?.name : '';
};

export const getCollectionNameById = (categories: CollectionModel[], id: number) => {
    const match = categories.find((el) => el.id === id);
    return match?.title ? match?.title : 'Select...';
};

export const getScoreNameByScoreCategory = (scoreCategory: ScoreCategory) => {
    switch (scoreCategory) {
        case ScoreCategory.LOW:
            return ScoreNames.DECREASED;
        case ScoreCategory.NORMAL:
            return ScoreNames.AVERAGE;
        case ScoreCategory.HIGH:
            return ScoreNames.INCREASED;
        default:
            return ScoreNames.LOCKED;
    }
};

export const getStatusNameByStatusType = (status: PractitionerSessionStatus) => {
    switch (status) {
        case PractitionerSessionStatus.SESSION_STATUS_REQUESTED: {
            return translate('sessions.status.scheduled');
        }
        case PractitionerSessionStatus.SESSION_STATUS_ACCEPTED: {
            return translate('sessions.status.accepted');
        }
        case PractitionerSessionStatus.SESSION_STATUS_DECLINED: {
            return translate('sessions.status.declined');
        }
        case PractitionerSessionStatus.SESSION_STATUS_COMPLETED: {
            return translate('sessions.status.completed');
        }
    }
};

export const getSessionTypeArray = (sessionType: string, sessions: SessionModel[]) => {
    const pendingSessions = sessions.filter((s) => s.status === PractitionerSessionStatus.SESSION_STATUS_REQUESTED);
    const inProgressSessions = sessions.filter((s) => s.status === PractitionerSessionStatus.SESSION_STATUS_ACCEPTED);
    const completedSessions = sessions.filter((s) => s.status === PractitionerSessionStatus.SESSION_STATUS_COMPLETED);

    return sessionType === SessionStatusCategories.COMPLETED
        ? completedSessions
        : sessionType === SessionStatusCategories.PENDING
        ? pendingSessions
        : inProgressSessions;
};

export const checkIfSessionIsScheduledOrDeclined = (session: SessionModel) => {
    return (
        session.status === PractitionerSessionStatus.SESSION_STATUS_REQUESTED ||
        session.status === PractitionerSessionStatus.SESSION_STATUS_DECLINED
    );
};

export const checkIfSessionIsScheduled = (session: SessionModel) => {
    return session.status === PractitionerSessionStatus.SESSION_STATUS_REQUESTED;
};

export const getHighlightedMineralsFromUsersResults = (results: ResultModel[]) => {
    const mineralsList = [
        'Calcium',
        'Phosphorus',
        'Potassium',
        'Sodium',
        'Chloride',
        'Magnesium',
        'Iron',
        'Zinc',
        'Iodine',
        'Chromium',
        'Copper',
        'Fluoride',
        'Molybdenum',
        'Manganese',
        'Selenium',
    ];

    const mineralsArray = [];
    for (let i = 0; i < mineralsList.length; i++) {
        for (let j = 0; j < results.length; j++) {
            if (results[j].swabComponent.title === mineralsList[i]) {
                mineralsArray.push(results[j]);
            }
        }
    }
    return mineralsArray;
};

export const getHighlightedVitaminsFromUsersResults = (results: ResultModel[]) => {
    return results.filter(
        (r) => r.swabComponent.title.includes('Vitamin') && !r.swabComponent.title.includes('(skincare related)'),
    );
};

export const getCollectionImage = (collection: CollectionModel, collectionList: BioCerticaCollectionModel[]) => {
    return collectionList.find((c) => collection.title === c.title)?.image;
};

export const getBadgeVariantForScoreCategory = (scoreCategory: ScoreCategory): BadgeVariantsType => {
    return scoreCategory === ScoreCategory.HIGH
        ? BadgeVariantsType.GREEN
        : scoreCategory === ScoreCategory.LOW
        ? BadgeVariantsType.RED
        : BadgeVariantsType.YELLOW;
};

export const hasDefaultValues = (questionnaire: QuestionnarieModel) => {
    return questionnaire === new QuestionnarieModel();
};

export const removeDuplicates = (arr: any[]) => {
    if (isNotNullOrUndefined(arr)) {
        return arr.filter((item, index) => arr.indexOf(item) === index);
    }

    return [];
};

export const soldKitsTableFields = () => {
    return [
        {
            key: 'id',
            label: 'Kit',
        },
        {
            key: 'user',
            label: 'Sold to',
        },
        {
            key: 'date',
            label: 'Date',
        },
        {
            key: 'discountCode',
            label: 'Discount code',
        },
        {
            key: 'earnings',
            label: 'Earnings',
        },
        {
            key: 'profit',
            label: 'Profit',
        },
    ];
};

export interface IMetabolizerStatus {
    img: any;
    title: string;
    color: string;
}

export const getMetabolizerStatus = (status: string): IMetabolizerStatus => {
    switch (status) {
        case MetabolizerStatuses.POOR:
            return { img: Poor, title: 'Poor', color: theme.colors.v2.light.englishVermillion.default };
        case MetabolizerStatuses.RAPID:
            return { img: Rapid, title: 'Rapid', color: theme.colors.v2.light.englishVermillion.default };
        case MetabolizerStatuses.INTERMEDIATE:
            return { img: Intermediate, title: 'Intermediate', color: theme.colors.v2.light.yellow.default };
        default:
            return { img: Normal, title: 'Normal', color: theme.colors.v2.light.green.default };
    }
};

export interface IMetabolizerStatus {
    img: any;
    title: string;
    color: string;
}

export const featureFlag = process.env.REACT_APP_BUILD_TARGET === 'PRODUCTION';

export const getQuickViewFields = (drugCategory: any[], drugClass: any[], genericName: any[]) => {
    return [
        {
            key: 'category',
            label: 'Category',
            icon: TherapyCategory,
            filter: true,
            caption: translate('reports.category.caption'),
            options: drugCategory,
        },
        {
            key: 'class',
            label: 'Therapeutic class',
            icon: TherapeuticClass,
            filter: true,
            caption: translate('reports.therapy.caption'),
            options: drugClass,
        },
        {
            key: 'genericName',
            label: 'Name',
            icon: PillIcon,
            filter: true,
            caption: translate('reports.name.caption'),
            options: genericName,
        },
        {
            key: 'usName',
            label: 'US name',
            icon: USflag,
            filter: false,
        },

        {
            key: 'saName',
            label: 'SA name',
            icon: SouthAfrica,
            filter: false,
        },

        {
            key: 'gene',
            label: 'Genes',
            icon: Genes,
            filter: false,
            caption: translate('reports.genes.caption'),
        },
        {
            key: 'phenotypeCategory',
            label: 'Phenotype category',
            icon: Metabolizer,
            filter: false,
        },
        {
            key: 'variantHaplotype',
            label: 'Genetic Variant',
            icon: Metabolizer,
            filter: false,
        },
        {
            key: 'sampleGenotype',
            label: 'Your Genotype',
            icon: Metabolizer,
            filter: false,
        },
        {
            key: 'genotypeAllele',
            label: 'genotype_allele',
            icon: Metabolizer,
            filter: false,
        },
        {
            key: 'refAllele',
            label: 'ref_allele',
            icon: Metabolizer,
            filter: false,
        },
        {
            key: 'altAllele',
            label: 'alt_allele',
            icon: Metabolizer,
            filter: false,
        },
    ];
};

export const getCustomFilteringFields = (
    phenotypeCategory: string[],
    specialtyPopulation: string[],
    levelOfEvidence: string[],
    disease: string[],
) => {
    return [
        {
            key: 'phenotypeCategory',
            label: 'Phenotype category',
            icon: PhenotypeCategory,
            caption: 'Type of drug effect on phenotype. Options: Toxicity, efficacy, dosage, metabolism/PK, PD, other.',
            options: removeDuplicates(phenotypeCategory),
        },
        {
            key: 'specialtyPopulation',
            label: 'Specialty population',
            icon: SpecialtyPopulation,
            caption: 'Description of a specialty population (e.g. Pediatric) in any supporting variant annotation.',
            options: removeDuplicates(specialtyPopulation),
        },
        {
            key: 'levelOfEvidence',
            label: 'Level of evidence',
            icon: LevelOfEvidence,
            caption: 'Levels 1-4 with 1 being the highest level of evidence that supports annotation text.',
            options: removeDuplicates(levelOfEvidence),
        },
        {
            key: 'diseasePhenotype',
            label: 'Disease',
            icon: Disease,
            caption:
                'Diseases/phenotypes in variant/allele-drug association. For example, if association was found in patients with a particular phenotype, or if the variant/allele-drug combination causes particular phenotype.',
            options: removeDuplicates(disease),
        },
    ];
};

export const filteringOptions: IFilteringOption[] = [
    {
        id: ReportsTypes.QUICK_VIEW,
        name: translate('reports.quick'),
        activeIcon: QuickView,
        inactiveIcon: QuickViewInactive,
    },
    {
        id: ReportsTypes.CUSTOM_FILTERING,
        name: translate('reports.custom'),
        activeIcon: CustomFiltering,
        inactiveIcon: CustomFilteringInactive,
    },
];

export const defaultPaginator = (): IPaginator => {
    return {
        currentEntriesSize: 0,
        filter: '',
        offset: 0,
        orderBy: '',
        groupBy: '',
        orderDir: '',
        page: 1,
        perPage: 0,
        totalEntriesSize: 0,
        totalPages: 1,
    };
};
